/**
 * Estados do TARP v5.10 + gating de perfil — domínio puro (GUPM-169).
 * ------------------------------------------------------------------
 * Fonte canônica do ciclo de vida de um `tarp_record`. Espelha 1:1 o
 * `tarp_status_enum` no banco e a RLS de encerramento.
 *
 * Fluxo v5.10 (GUPM-178 acrescenta o rascunho EM_ABERTURA na frente):
 *   EM_ABERTURA (rascunho persistente; quem abriu descarta)
 *     → ABERTA (confirmada; qualquer um confirma)
 *     → CONCLUIDA_AGUARDANDO (operador conclui e encaminha ao RT)
 *     → EM_ANALISE_RT (RT assume) → ENCERRADA (baixa técnica do RT)
 *
 * Regra de domínio #4: operador NUNCA encerra. A RLS é a fonte da verdade
 * (camada 2); `canClose()` só decide se a UI mostra o botão (camada 1).
 *
 * TS puro: sem React, sem Supabase.
 */

export type TarpRecordStatus =
  | 'EM_ABERTURA'
  | 'ABERTA'
  | 'EM_ANALISE_RT'
  | 'CONCLUIDA_AGUARDANDO'
  | 'ENCERRADA';

/** Perfis de autorização (espelha `user_profile_enum` + admin). */
export type AppProfile = 'operador' | 'tecnico_responsavel' | 'admin';

export const STATUS_LABEL: Record<TarpRecordStatus, string> = {
  EM_ABERTURA: 'Em abertura',
  ABERTA: 'Aberta',
  EM_ANALISE_RT: 'Em análise (RT)',
  CONCLUIDA_AGUARDANDO: 'Aguardando RT',
  ENCERRADA: 'Baixada',
};

/**
 * Estados em que o TARP ainda está ativo (aparece nas listas de abertos).
 * Inclui EM_ABERTURA (GUPM-178 Fase B): o rascunho persistente aparece na lista
 * pra todos com tarja "Em abertura". O valor já existe no enum do banco
 * (migration 20260720140000), então consultar por ele não quebra a query.
 */
export const OPEN_STATUSES: TarpRecordStatus[] = [
  'EM_ABERTURA',
  'ABERTA',
  'EM_ANALISE_RT',
  'CONCLUIDA_AGUARDANDO',
];

/** ENCERRADA é o único estado terminal. */
export function isOpen(status: TarpRecordStatus): boolean {
  return status !== 'ENCERRADA';
}

/**
 * Só técnico responsável/admin dão baixa (encerram). Espelha a RLS
 * `tarp_records_update` (transição para ENCERRADA). A UI usa para esconder
 * o botão de baixa do operador.
 */
export function canClose(profile: AppProfile | null | undefined): boolean {
  return profile === 'tecnico_responsavel' || profile === 'admin';
}

/**
 * Estados a partir dos quais a baixa (→ ENCERRADA) é válida: só os pós-handoff.
 * O operador precisa ter concluído e encaminhado antes — encerrar uma TARP ainda
 * ABERTA/EM_ABERTURA puleria o protocolo do operador (integridade da máquina de
 * estados). `EM_ANALISE_RT` fica incluído por compatibilidade (estado legado no
 * enum; deixou de ser transitado no v5.10). Reforçado server-side pelo trigger
 * `tarp_records_write_guard` — GUPM-184 self-review (F1). Camada 1 (esconde a UI);
 * o trigger/RLS são a fonte da verdade (camada 2).
 */
export function canReceberBaixa(status: TarpRecordStatus): boolean {
  return status === 'CONCLUIDA_AGUARDANDO' || status === 'EM_ANALISE_RT';
}

// ── Estágio do ciclo — agregação para o resumo do dashboard (GUPM-325) ──────

/**
 * Os três ESTÁGIOS do ciclo de vida, do ponto de vista de quem está de plantão:
 * a TARP está comigo, está com o RT, ou acabou.
 *
 * Não substitui `TarpRecordStatus` — é uma projeção dele. O estado individual
 * continua sendo a verdade (a lista de TARPs ativas mostra a tarja por estado);
 * o estágio existe para o CONTADOR do dashboard, onde quatro pills por estado
 * davam duas colunas estruturalmente zero e escondiam uma população real.
 */
export type EstagioTarp = 'EM_ABERTURA' | 'AGUARDANDO_RT' | 'ENCERRADA';

/**
 * Estado → estágio. **`Record`, jamais lista literal.**
 *
 * O `tsc` obriga uma entrada para cada valor de `TarpRecordStatus`: estado novo no
 * enum quebra a compilação exatamente aqui, que é onde tem de quebrar. Duas vezes
 * neste repo (GUPM-303 e GUPM-307) uma lista literal do mesmo domínio deixou passar
 * um estado novo sem o compilador acusar, e o defeito só apareceu na tela.
 *
 * Os pares:
 * - `EM_ABERTURA` + `ABERTA` — a TARP está com quem abriu, antes do handoff. As 4
 *   `EM_ABERTURA` do banco (TARPs do INA-12A) não apareciam em contador nenhum.
 * - `CONCLUIDA_AGUARDANDO` + `EM_ANALISE_RT` — está com o RT. O segundo é legado
 *   (não é mais transitado no v5.10); somá-lo aqui mantém visível uma TARP presa
 *   nele sem gastar uma pill que seria sempre zero.
 */
export const ESTAGIO_TARP: Record<TarpRecordStatus, EstagioTarp> = {
  EM_ABERTURA: 'EM_ABERTURA',
  ABERTA: 'EM_ABERTURA',
  CONCLUIDA_AGUARDANDO: 'AGUARDANDO_RT',
  EM_ANALISE_RT: 'AGUARDANDO_RT',
  ENCERRADA: 'ENCERRADA',
};

/**
 * Rótulo de tela de cada estágio. "Aguardando RT" espelha `STATUS_LABEL`.
 *
 * `ENCERRADA` lê **"Baixadas"** porque nomeia o ato: `nextStatusForAction('close', …)`
 * é a única transição que produz o estado, e `closeTarp` sempre grava o parecer, o
 * enquadramento ANM, `closed_by`, `closed_at` e `baixa_totp_verified` (a baixa exige
 * TOTP — assinatura de fato). Não existe TARP encerrada sem baixa, então o rótulo é
 * exato para toda a população, não aproximação.
 *
 * É também a palavra que o app já usava no ato — botão "Dar Baixa", autoria "Baixa
 * por", `closure_comment` = "parecer da baixa técnica do RT" — enquanto a MESMA
 * população se chamava "Encerradas" aqui, "Encerrada" na tarja e "fechadas" no painel
 * de auditoria do M3: três nomes para um estado. Preso pelo teste
 * `rotulo-de-encerrada-fala-baixa-nas-duas-telas`, que assere os dois mapas juntos.
 *
 * O **enum do banco segue `ENCERRADA`** (nenhuma migration): o termo formal fica no
 * dado — auditoria, exportação, retenção ANM — e a palavra de quem usa fica na tela.
 * Etapa 1 do padrão de nomenclatura; `Rascunho` (`EM_ABERTURA`), `Em campo`
 * (`ABERTA`) e o conjunto "em curso" vêm em card próprio, com ADR.
 */
export const ESTAGIO_LABEL: Record<EstagioTarp, string> = {
  EM_ABERTURA: 'Em abertura',
  AGUARDANDO_RT: 'Aguardando RT',
  ENCERRADA: 'Baixadas',
};

/**
 * Soma um mapa de contagens por estado em contagens por estágio.
 *
 * Aceita `Partial` porque é essa a forma que o dashboard tem em mãos (status sem
 * nenhuma TARP não aparece no mapa) e devolve **sempre os três estágios em zero**,
 * nunca `undefined` — a pill precisa exibir `0`, não vazio.
 */
export function agruparPorEstagio(
  porStatus: Partial<Record<TarpRecordStatus, number>>,
): Record<EstagioTarp, number> {
  const total: Record<EstagioTarp, number> = {
    EM_ABERTURA: 0, AGUARDANDO_RT: 0, ENCERRADA: 0,
  };
  for (const [status, n] of Object.entries(porStatus) as [TarpRecordStatus, number | undefined][]) {
    // Chave desconhecida (mapa vindo de dado cru) não pode derrubar o card nem cair
    // silenciosamente num estágio errado: fica fora da soma, como já ficava antes.
    const estagio = ESTAGIO_TARP[status];
    if (estagio) total[estagio] += n ?? 0;
  }
  return total;
}
